import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import type { FileNode } from "../api.ts";
import { useMoving, useNodeDrag, useNodeDrop } from "../lib/dragMove.ts";
import { carriesFiles, draggedCount, setHoverTarget } from "../lib/drop.ts";
import { formatBytes, formatCount, formatExactBytes, formatShortDate } from "../lib/format.ts";
import { kindOf } from "../lib/kinds.ts";
import { useLanded } from "../lib/landed.ts";
import { PENDING_PREFIX } from "../lib/queries.ts";
import { enqueue, type UploadTarget } from "../lib/uploads.ts";
import { locationOf } from "./Breadcrumbs.tsx";
import { CopyButton } from "./CopyButton.tsx";
import { markDropHandled } from "./DropLayer.tsx";
import { FileBadge, FolderBadge } from "./FileBadge.tsx";
import styles from "./FileTable.module.css";
import { Icon } from "./Icon.tsx";
import { PickFiles } from "./PickFiles.tsx";
import ui from "./ui.module.css";

export const viewersLabel = (count: number) =>
  count === 0 ? "Only you" : formatCount(count, "person", "people");

export const itemsLabel = (count: number) =>
  count === 0 ? "Empty" : formatCount(count, "item", "items");

export type SortKey = "name" | "size" | "updated";
export type Sort = { key: SortKey; direction: "asc" | "desc" };

const NATURAL: Record<SortKey, "asc" | "desc"> = { name: "asc", size: "desc", updated: "desc" };

export const SORT_CHOICES: { value: string; label: string; sort: Sort | null }[] = [
  { value: "added", label: "Date added", sort: null },
  { value: "name-asc", label: "Name A–Z", sort: { key: "name", direction: "asc" } },
  { value: "name-desc", label: "Name Z–A", sort: { key: "name", direction: "desc" } },
  { value: "size-desc", label: "Largest first", sort: { key: "size", direction: "desc" } },
  { value: "size-asc", label: "Smallest first", sort: { key: "size", direction: "asc" } },
  { value: "updated-desc", label: "Newest update", sort: { key: "updated", direction: "desc" } },
  { value: "updated-asc", label: "Oldest update", sort: { key: "updated", direction: "asc" } },
];

export const sortChoice = (sort: Sort | null): string =>
  sort ? `${sort.key}-${sort.direction}` : "added";

export const sortOfChoice = (value: string): Sort | null =>
  SORT_CHOICES.find((choice) => choice.value === value)?.sort ?? null;

const nextSort = (current: Sort | null, key: SortKey): Sort | null => {
  if (current?.key !== key) return { key, direction: NATURAL[key] };
  if (current.direction === NATURAL[key]) {
    return { key, direction: current.direction === "asc" ? "desc" : "asc" };
  }
  return null;
};

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

const compare = (a: FileNode, b: FileNode, key: SortKey) => {
  switch (key) {
    case "name":
      return collator.compare(a.name, b.name);
    case "size":
      return a.size - b.size;
    case "updated":
      return Date.parse(a.updatedAt) - Date.parse(b.updatedAt);
  }
};

const foldersFirst = (a: FileNode, b: FileNode) =>
  Number(b.kind === "folder") - Number(a.kind === "folder");

export const sortFiles = (nodes: FileNode[], sort: Sort | null): FileNode[] => {
  if (!sort) return nodes;
  const factor = sort.direction === "asc" ? 1 : -1;
  return [...nodes].sort(
    (a, b) =>
      foldersFirst(a, b) || compare(a, b, sort.key) * factor || collator.compare(a.name, b.name),
  );
};

export const labelOf = (node: FileNode): string =>
  node.kind === "folder" ? "Folder" : kindOf(node.contentType, node.name).label;

export const filterFiles = (nodes: FileNode[], query: string): FileNode[] => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return nodes;
  return nodes.filter((node) => {
    const haystack = `${node.name} ${labelOf(node)}`.toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
};

type Column = { key: string; label: string; hidden?: boolean };

/** The sheet, header row and row group every listing shares. */
export function TableShell({
  className,
  label,
  columns,
  children,
}: {
  className?: string;
  label: string;
  columns: Column[];
  children: ReactNode;
}) {
  return (
    <div className={styles.frame}>
      <table className={`${styles.table} ${className ?? ""}`} role="table" aria-label={label}>
        <thead role="rowgroup" className={styles.head}>
          <tr role="row">
            {columns.map((column) => (
              <th key={column.key} scope="col" role="columnheader" data-col={column.key}>
                <span className={column.hidden ? ui.visuallyHidden : styles.headerLabel}>
                  {column.label}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody role="rowgroup" className={styles.body}>
          {children}
        </tbody>
      </table>
    </div>
  );
}

export function Row({ folder, children }: { folder?: boolean; children: ReactNode }) {
  return (
    <tr role="row" className={styles.row} data-kind={folder ? "folder" : undefined}>
      {children}
    </tr>
  );
}

export function Cell({
  col,
  label,
  className,
  children,
}: {
  col: string;
  label?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <td role="cell" className={className ?? styles.fact} data-col={col}>
      {label ? (
        <span className={styles.label} aria-hidden="true">
          {label}
        </span>
      ) : null}
      {children}
    </td>
  );
}

type SortProps = { sort: Sort | null; onSort: (sort: Sort | null) => void };
type TableProps = { nodes: FileNode[]; showLocation?: boolean } & SortProps;

export function FileTable({ nodes, sort, onSort, showLocation = false }: TableProps) {
  return (
    <div className={styles.frame}>
      <table className={styles.table} role="table">
        <thead role="rowgroup" className={styles.head}>
          <tr role="row">
            <SortHeader column="name" label="Name" sort={sort} onSort={onSort} />
            <SortHeader column="size" label="Size" sort={sort} onSort={onSort} align="end" />
            <th scope="col" role="columnheader" data-col="version">
              <span className={styles.headerLabel}>Version</span>
            </th>
            <th scope="col" role="columnheader" data-col="viewers">
              <span className={styles.headerLabel}>Viewers</span>
            </th>
            <SortHeader column="updated" label="Updated" sort={sort} onSort={onSort} />
            <th scope="col" role="columnheader" data-col="actions">
              <span className={ui.visuallyHidden}>Actions</span>
            </th>
          </tr>
        </thead>
        <tbody role="rowgroup" className={styles.body}>
          {nodes.map((node) =>
            node.kind === "folder" ? (
              <FolderRow key={node.id} folder={node} showLocation={showLocation} />
            ) : (
              <FileRow key={node.id} file={node} showLocation={showLocation} />
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}

function SortHeader({
  column,
  label,
  align,
  sort,
  onSort,
}: { column: SortKey; label: string; align?: "end" } & SortProps) {
  const direction = sort?.key === column ? sort.direction : null;
  return (
    <th
      scope="col"
      role="columnheader"
      data-col={column}
      data-align={align}
      aria-sort={direction ? (direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        className={styles.sortButton}
        data-sorted={direction ? "" : undefined}
        onClick={() => onSort(nextSort(sort, column))}
      >
        {label}
        <span className={styles.sortArrow} data-direction={direction ?? undefined}>
          <Icon name="chevron" />
        </span>
      </button>
    </th>
  );
}

function ViewersCell({ node }: { node: FileNode }) {
  return (
    <td
      role="cell"
      className={styles.fact}
      data-col="viewers"
      data-viewers={node.public ? "public" : node.viewerCount > 0 ? "shared" : undefined}
    >
      <span className={styles.label} aria-hidden="true">
        Viewers
      </span>
      {node.public ? (
        <span className={styles.viewers} title="Anyone with the link">
          <Icon name="globe" />
          Public
        </span>
      ) : (
        <span className={styles.viewers}>
          {node.viewerCount > 0 ? <Icon name="people" /> : <Icon name="lock" />}
          {viewersLabel(node.viewerCount)}
        </span>
      )}
    </td>
  );
}

function UpdatedCell({ node }: { node: FileNode }) {
  return (
    <td role="cell" className={styles.fact} data-col="updated">
      <span className={styles.label} aria-hidden="true">
        Updated
      </span>
      <time dateTime={node.updatedAt}>{formatShortDate(node.updatedAt)}</time>
    </td>
  );
}

function FolderRow({ folder, showLocation }: { folder: FileNode; showLocation: boolean }) {
  const [dropping, setDropping] = useState(false);
  const pending = folder.id.startsWith(PENDING_PREFIX);
  const target: UploadTarget = { kind: "new", parentId: folder.id };
  const drag = useNodeDrag(folder, !pending);
  const land = useNodeDrop({ parentId: folder.id, name: folder.name }, !pending);
  const moving = useMoving()?.node.id === folder.id;

  return (
    <tr
      role="row"
      className={styles.row}
      data-kind="folder"
      data-pending={pending || undefined}
      data-dropping={dropping || land.over || undefined}
      data-dragging={drag.dragging || undefined}
      data-moving={moving || undefined}
      {...drag.props}
      onDragOver={(event) => {
        if (land.dragOver(event) || pending || !carriesFiles(event)) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = "copy";
        setHoverTarget(target, folder.name);
        setDropping(true);
      }}
      onDragLeave={(event) => {
        land.dragLeave(event);
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropping(false);
      }}
      onDrop={(event) => {
        if (land.drop(event)) return;
        if (pending || !carriesFiles(event) || event.dataTransfer.files.length === 0) return;
        event.preventDefault();
        event.stopPropagation();
        setDropping(false);
        markDropHandled();
        enqueue([...event.dataTransfer.files], target);
      }}
    >
      <td role="cell" className={styles.nameCell} data-col="name">
        <div className={styles.nameInner} data-drag-image>
          <FolderBadge />
          <div className={styles.nameText}>
            {pending ? (
              <span className={styles.name}>{folder.name}</span>
            ) : (
              <Link
                to="/folders/$id"
                params={{ id: folder.id }}
                className={styles.name}
                style={{ viewTransitionName: `file-${folder.id}` }}
              >
                {folder.name}
              </Link>
            )}
            <span className={styles.typeInline}>{pending ? "Creating…" : "Folder"}</span>
            {showLocation ? (
              <span className={styles.location}>{locationOf(folder.ancestors)}</span>
            ) : null}
            <RowNote
              note={
                moving
                  ? "Moving…"
                  : land.over
                    ? `Drop to move into ${folder.name}`
                    : dropping
                      ? `Drop to upload into ${folder.name}`
                      : null
              }
            />
          </div>
        </div>
      </td>
      <td role="cell" className={styles.fact} data-col="items" colSpan={2}>
        <span className={styles.label} aria-hidden="true">
          Items
        </span>
        <span>{itemsLabel(folder.childCount)}</span>
      </td>
      <ViewersCell node={folder} />
      <UpdatedCell node={folder} />
      <td role="cell" className={styles.actions} data-col="actions">
        {pending ? null : (
          <CopyButton value={folder.url} label={`Copy link to ${folder.name}`} compact />
        )}
        <span className={styles.open} aria-hidden="true">
          <Icon name="chevron" />
        </span>
      </td>
    </tr>
  );
}

function RowNote({ note }: { note: string | null }) {
  return note ? <span className={styles.dropNote}>{note}</span> : null;
}

function FileRow({ file, showLocation }: { file: FileNode; showLocation: boolean }) {
  const [dropping, setDropping] = useState(false);
  const target: UploadTarget = {
    kind: "version",
    nodeId: file.id,
    name: file.name,
    parentId: file.parentId,
  };
  const kind = kindOf(file.contentType, file.name);
  const drag = useNodeDrag(file);
  const moving = useMoving()?.node.id === file.id;
  const landed = useLanded().has(file.id);

  return (
    <tr
      role="row"
      className={styles.row}
      data-dropping={dropping || undefined}
      data-dragging={drag.dragging || undefined}
      data-moving={moving || undefined}
      data-landed={landed || undefined}
      {...drag.props}
      onDragOver={(event) => {
        if (!carriesFiles(event) || draggedCount(event) > 1) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = "copy";
        setHoverTarget(target);
        setDropping(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropping(false);
      }}
      onDrop={(event) => {
        if (!carriesFiles(event) || event.dataTransfer.files.length !== 1) return;
        event.preventDefault();
        event.stopPropagation();
        setDropping(false);
        markDropHandled();
        enqueue([...event.dataTransfer.files], target);
      }}
    >
      <td role="cell" className={styles.nameCell} data-col="name">
        <div className={styles.nameInner} data-drag-image>
          <FileBadge contentType={file.contentType} name={file.name} />
          <div className={styles.nameText}>
            <Link
              to="/files/$id"
              params={{ id: file.id }}
              className={styles.name}
              style={{ viewTransitionName: `file-${file.id}` }}
            >
              {file.name}
            </Link>
            <span className={styles.typeInline}>{kind.label}</span>
            {showLocation ? (
              <span className={styles.location}>{locationOf(file.ancestors)}</span>
            ) : null}
            <RowNote
              note={
                moving
                  ? "Moving…"
                  : dropping
                    ? `Drop to add version ${file.versionCount + 1}`
                    : null
              }
            />
          </div>
        </div>
      </td>
      <td role="cell" className={styles.fact} data-col="size" data-align="end">
        <span className={styles.label} aria-hidden="true">
          Size
        </span>
        <span title={formatExactBytes(file.size)}>{formatBytes(file.size)}</span>
      </td>
      <td role="cell" className={styles.fact} data-col="version">
        <span className={styles.label} aria-hidden="true">
          Version
        </span>
        <span>
          v{file.currentVersion ?? "–"}
          {file.versionCount > 1 ? (
            <span className={styles.subtle}> of {file.versionCount}</span>
          ) : null}
        </span>
      </td>
      <ViewersCell node={file} />
      <UpdatedCell node={file} />
      <td role="cell" className={styles.actions} data-col="actions">
        <CopyButton value={file.url} label={`Copy link to ${file.name}`} compact />
        <PickFiles
          target={target}
          label={`Upload a new version of ${file.name}`}
          variant="ghost"
          compact
        >
          <Icon name="version" />
        </PickFiles>
      </td>
    </tr>
  );
}
